import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useQueryClient,
} from "@tanstack/react-query";
import {
  fetchOrderHistories,
  fetchOrderHistoryDetails,
  OrderHistoryModel,
  OrderWithHistoryDetailModel,
} from "../repositories/history.repository";

export const orderHistoriesKey = (cusId: string | undefined): QueryKey => [
  "orderHistories",
  { cusId },
];

export function useOrderHistories(
  cusId: string | undefined,
  options?: Omit<
    UseQueryOptions<OrderHistoryModel[], Error, OrderHistoryModel[], QueryKey>,
    "queryKey" | "queryFn" | "enabled"
  >
) {
  const qc = useQueryClient();

  const query = useQuery<
    OrderHistoryModel[],
    Error,
    OrderHistoryModel[],
    QueryKey
  >({
    queryKey: orderHistoriesKey(cusId),
    queryFn: () => fetchOrderHistories(cusId as string),
    enabled: !!cusId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    ...options,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: orderHistoriesKey(cusId) });

  return { ...query, invalidate };
}

/* ===================== */
/*      DETAILS HOOK     */
/* ===================== */

export const orderHistoryDetailsKey = (
  orderUuid: string | undefined
): QueryKey => ["orderHistoryDetails", { orderUuid }];

export function useOrderHistoryDetails(
  orderUuid: string | undefined,
  chanelId: string,
  options?: Omit<
    UseQueryOptions<
      OrderWithHistoryDetailModel,
      Error,
      OrderWithHistoryDetailModel,
      QueryKey
    >,
    "queryKey" | "queryFn" | "enabled"
  >
) {
  const qc = useQueryClient();

  const query = useQuery<
    OrderWithHistoryDetailModel,
    Error,
    OrderWithHistoryDetailModel,
    QueryKey
  >({
    queryKey: orderHistoryDetailsKey(orderUuid),
    queryFn: () => fetchOrderHistoryDetails(orderUuid as string, chanelId),
    enabled: !!orderUuid,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    ...options,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: orderHistoryDetailsKey(orderUuid) });

  const prefetch = async (id: string) =>
    qc.prefetchQuery({
      queryKey: orderHistoryDetailsKey(id),
      queryFn: () => fetchOrderHistoryDetails(id, chanelId),
      staleTime: 60_000,
    });

  const setCached = (data: OrderWithHistoryDetailModel) =>
    qc.setQueryData(orderHistoryDetailsKey(orderUuid), data);

  return { ...query, invalidate, prefetch, setCached };
}
